import { useQuery } from "@tanstack/react-query";

import {
  CategoryDetailParams,
  getCategory,
} from "../../services/productPlaneApi";
import { CategoryDetail } from "../../types/categoryDetail";
import { queryKeys } from "./keys";
import { planeErrorMessage } from "./planeError";

// Sources asked for with a category: the most the backend gives in one page.
// The source table pages them itself, and a source mark finds its row there.
export const CATEGORY_SOURCES_LIMIT = 200;

// Products on a page of the category's product list
export const CATEGORY_PRODUCTS_PAGE_SIZE = 12;

// Narrower classes asked for: the most the backend gives in one page
export const CATEGORY_NARROWER_LIMIT = 200;

interface UseCategoryResult {
  category: CategoryDetail | null;
  loading: boolean;
  // Why the category cannot be shown; null while it can, or while it loads
  error: string | null;
}

/**
 * TanStack Query hook for one category.
 *
 * The view asks for a larger page of a list, or the next page of products,
 * by changing `params`; the category on screen stays while that loads.
 *
 * @param categoryId - The id of the category; null fetches nothing
 * @param params - The size and the place of the pages of its lists
 * @returns The category, the loading state and a message for a failed read
 */
export function useCategory(
  categoryId: string | null,
  params: CategoryDetailParams = {},
): UseCategoryResult {
  const request: CategoryDetailParams = {
    products_limit: CATEGORY_PRODUCTS_PAGE_SIZE,
    narrower_limit: CATEGORY_NARROWER_LIMIT,
    sources_limit: CATEGORY_SOURCES_LIMIT,
    ...params,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.productPlane.category(categoryId!, { ...request }),
    queryFn: () => getCategory(categoryId!, request),
    enabled: !!categoryId,
    staleTime: 2 * 60 * 1000,
    // Another category must not show the last one's lists
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === categoryId ? previous : undefined,
  });

  return {
    category: data ?? null,
    loading: isLoading,
    error: error ? planeErrorMessage(error, "Category") : null,
  };
}
